import { Component, input, output, computed, ChangeDetectionStrategy, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  type AgentChatUserMessageOnEditMessageProps,
  type AgentChatUserMessageOnSwitchToBranchProps,
} from "./ai-chat-user-message.types";
import { AgentChatUserMessageRenderer } from "./ai-chat-user-message-renderer";
import { AgentChatUserMessageCopyButton, AgentChatUserMessageEditButton } from "./ai-chat-user-message-buttons";
import { AgentChatUserMessageToolbar } from "./ai-chat-user-message-toolbar";
import { AgentChatUserMessageBranchNavigation } from "./ai-chat-user-message-branch-navigation";
import { cn } from "../utils/utils";
import { UserMessage } from "@ag-ui/core";

function flattenUserMessageContent(content?: UserMessage["content"]): string {
  if (!content) {
    return "";
  }
  if (typeof content === "string") {
    return content;
  }
  return content
    .map((part) => {
      if (
        part &&
        typeof part === "object" &&
        "type" in part &&
        (part as { type?: unknown }).type === "text" &&
        typeof (part as { text?: unknown }).text === "string"
      ) {
        return (part as { text: string }).text;
      }
      return "";
    })
    .filter((text) => text.length > 0)
    .join("\n");
}

@Component({
  standalone: true,
  selector: "agent-chat-user-message",
  host: { "data-agent-ui": "" },
  imports: [
    CommonModule,
    AgentChatUserMessageRenderer,
    AgentChatUserMessageCopyButton,
    AgentChatUserMessageEditButton,
    AgentChatUserMessageToolbar,
    AgentChatUserMessageBranchNavigation,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <div [class]="computedClass()" [attr.data-message-id]="message()?.id">
      <!-- Message Renderer -->
      <agent-chat-user-message-renderer [content]="flattenedContent()" [inputClass]="messageRendererClass()">
      </agent-chat-user-message-renderer>

      <!-- Toolbar -->
      <div agentChatUserMessageToolbar [inputClass]="toolbarClass()">
        <div class="achat-msg__actions">
          <!-- Additional toolbar items -->
          @if (additionalToolbarItems()) {
          <ng-container [ngTemplateOutlet]="additionalToolbarItems() || null"></ng-container>
          }

          <!-- Copy button -->
          <agent-chat-user-message-copy-button
            [content]="flattenedContent()"
            [inputClass]="copyButtonClass()"
            (clicked)="handleCopy()"
          >
          </agent-chat-user-message-copy-button>

          <!-- Edit button -->
          <agent-chat-user-message-edit-button [inputClass]="editButtonClass()" (clicked)="handleEdit()">
          </agent-chat-user-message-edit-button>

          <!-- Branch navigation -->
          @if (showBranchNavigation()) {
          <agent-chat-user-message-branch-navigation
            [currentBranch]="branchIndexValue()"
            [numberOfBranches]="numberOfBranchesValue()"
            [message]="message()!"
            [inputClass]="branchNavigationClass()"
            (switchToBranch)="handleSwitchToBranch($event)"
          >
          </agent-chat-user-message-branch-navigation>
          }
        </div>
      </div>
    </div>
  `,
})
export class AgentChatUserMessage {
  // Props for tweaking default components
  messageRendererClass = input<string | undefined>();

  toolbarClass = input<string | undefined>();

  copyButtonClass = input<string | undefined>();

  editButtonClass = input<string | undefined>();

  branchNavigationClass = input<string | undefined>();

  // Regular inputs
  message = input<UserMessage>();

  branchIndex = input<number | undefined>();

  numberOfBranches = input<number | undefined>();

  additionalToolbarItems = input<any | undefined>();

  inputClass = input<string | undefined>();

  // Output events
  editMessage = output<AgentChatUserMessageOnEditMessageProps>();

  switchToBranch = output<AgentChatUserMessageOnSwitchToBranchProps>();

  // Derived values
  branchIndexValue = computed(() => this.branchIndex() ?? 0);

  numberOfBranchesValue = computed(() => this.numberOfBranches() ?? 1);

  showBranchNavigation = computed(() => (this.numberOfBranches() ?? 1) > 1);

  computedClass = computed(() => cn("achat-user-msg", this.inputClass()));

  flattenedContent = computed(() => flattenUserMessageContent(this.message()?.content));

  handleCopy(): void {}

  handleEdit(): void {
    this.editMessage.emit({ message: this.message()! });
  }

  handleSwitchToBranch(props: AgentChatUserMessageOnSwitchToBranchProps): void {
    this.switchToBranch.emit(props);
  }

  constructor() {}
}
